import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import FormModal from 'web-ui-kit/modals/FormModal';
import { BodyLargeStyles, BodySmallStyles, BodyBoldLargeStyles } from 'web-ui-kit/typography';
import { grayLightM } from 'web-ui-kit/colors';
import SecondaryButton from 'web-ui-kit/buttons/SecondaryButton';
import { modalButtonClicked, modalLinkClicked } from 'web-ui-kit/events';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject;

function HelpButtonLink(_ref) {
  var _ref$children = _ref.children,
      children = _ref$children === void 0 ? [] : _ref$children,
      _ref$href = _ref.href,
      href = _ref$href === void 0 ? '' : _ref$href;
  return (
    /*#__PURE__*/
    React.createElement(StyledLink, {
      href: href,
      target: "_blank",
      rel: "noopener noreferrer"
    }, children)
  );
}

var StyledLink = styled.a(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", ";\n  text-decoration: none;\n"])), BodyLargeStyles);

var _templateObject$1, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;
/**
 * A mobile variant of Form modal which allows the user to navigate on click on some listed links/buttons.
 *
 * The modal overlay will cover the entire viewport and prevent body scrolling.
 *
 * The modal close event will be dispatched when:
 *  (1) the overlay is clicked
 *  (2) the escape key is pressed
 *  (3) the xout icon is clicked
 *  (4) a link/button is clicked
 */


var VARIANTS = {
  "default": {
    width: '20rem'
  }
};

function NavigationModal(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? 'navigation' : _ref$id,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? '' : _ref$title,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'default' : _ref$variant,
      _ref$links = _ref.links,
      links = _ref$links === void 0 ? [] : _ref$links,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("a", null, children)
    );
  } : _ref$linkComponent,
      _ref$buttons = _ref.buttons,
      buttons = _ref$buttons === void 0 ? [] : _ref$buttons;
  var Description = styles.Description,
      LinkContainer = styles.LinkContainer,
      StyledFormModal = styles.StyledFormModal,
      SubContainer = styles.SubContainer,
      Title = styles.Title,
      PaddedContent = styles.PaddedContent;
  var LinkComponent = linkComponent;

  function handleClick(_ref3) {
    var event = _ref3.event,
        button = _ref3.button,
        _ref3$link = _ref3.link,
        link = _ref3$link === void 0 ? {} : _ref3$link,
        type = _ref3.type;
    var payload = null;

    switch (type) {
      case modalLinkClicked:
        var _ref4 = link || {},
            route = _ref4.route,
            linkText = _ref4.label;

        payload = {
          event: event,
          route: route,
          text: linkText
        };
        break;

      case modalButtonClicked:
        var _ref5 = button || {},
            buttonId = _ref5.id,
            text = _ref5.label;

        payload = {
          buttonId: buttonId,
          event: event,
          text: text
        };
        break;
    }

    payload && dispatch({
      type: type,
      payload: payload
    });
  }

  return (
    /*#__PURE__*/
    React.createElement(StyledFormModal, {
      className: className,
      dispatch: dispatch,
      id: id,
      modalWidth: VARIANTS[variant].width || VARIANTS["default"].width
    },
    /*#__PURE__*/
    React.createElement(Title, null, title),
    /*#__PURE__*/
    React.createElement(PaddedContent, null, links.map(function (link) {
      var _ref6 = link || {},
          id = _ref6.id,
          route = _ref6.route,
          label = _ref6.label;

      var key = "".concat(id, "-link");
      return (
        /*#__PURE__*/
        React.createElement(LinkContainer, {
          key: key,
          onClick: function onClick(event) {
            return handleClick({
              event: event,
              link: link,
              type: modalLinkClicked
            });
          }
        },
        /*#__PURE__*/
        React.createElement(LinkComponent, {
          route: route,
          styledLink: HelpButtonLink
        }, label))
      );
    })),
    /*#__PURE__*/
    React.createElement(PaddedContent, null, buttons.map(function (button) {
      var _ref7 = button || {},
          description = _ref7.description,
          Icon = _ref7.Icon,
          label = _ref7.label,
          id = _ref7.id;

      var key = "".concat(id, "-button");
      return (
        /*#__PURE__*/
        React.createElement(SubContainer, {
          key: key
        },
        /*#__PURE__*/
        React.createElement(Description, null, description),
        /*#__PURE__*/
        React.createElement(SecondaryButton, {
          id: id,
          labelText: label,
          Icon: Icon,
          dispatch: function dispatch(event) {
            return handleClick({
              event: event,
              button: button,
              type: modalButtonClicked
            });
          }
        }))
      );
    })))
  );
}

NavigationModal.propTypes = {
  /** Allows extending styles with styled(NavigationModal) constructor */
  className: PropTypes.string,

  /** For reporting events up to parent */
  dispatch: PropTypes.func,

  /**
   * An optional id string that can be used
   * to help identify the origin of modal events.
   * This string is not user visible.
   */
  id: PropTypes.string,

  /** The modal title */
  title: PropTypes.string,

  /** Optional variant for styling */
  variant: PropTypes.oneOf(['default']),

  /** Navigation links */
  links: PropTypes.array,

  /** To handle client and/or server side routing from the parent */
  linkComponent: PropTypes.func,

  /** Navigation buttons */
  buttons: PropTypes.array
};
var styles = {
  PaddedContent: styled.div(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n    display: flex;\n    flex-direction: column;\n    padding-left: 1rem;\n    padding-right: 1rem;\n    padding-bottom: 0.875rem;\n    border-top: 0.5px solid ", ";\n  "])), grayLightM),
  Description: styled.p(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", "\n    padding-top: 1.25rem;\n    padding-bottom: 0.5rem;\n  "])), BodySmallStyles),
  LinkContainer: styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    ", "\n    cursor: pointer;\n    padding-top: 0.75rem;\n    padding-bottom: 0.75rem;\n  "])), BodyLargeStyles),
  StyledFormModal: styled(FormModal)(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    ", "\n  "])), function (p) {
    return p.id && "\n      .".concat(p.id, "-modal-container {\n        width: ").concat(p.modalWidth, ";\n        height: 100%;\n      }\n\n      #").concat(p.id, "-modal {\n        padding: 0;\n      }\n    ");
  }),
  SubContainer: styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    display: flex;\n    flex-direction: column;\n  "]))),
  Title: styled.h2(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    ", ";\n    margin: 1rem auto;\n    text-align: center;\n  "])), BodyBoldLargeStyles)
};
export default NavigationModal;